import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllInfrastructures,
  deleteInfrastructure,
  createInfrastructure,
  updateInfrastructure,
} from "../../../../../services/infrastructuresServices";

import {
  extractArray,
  getStats,
  isDemoMode,
  normalizeText,
} from "../utils/infrastructuresManagerUtils";

const CHUNK = 50;

export default function useInfrastructuresManager() {
  const [infrastructures, setInfrastructures] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [gpsFilter, setGpsFilter] = useState("");

  const [visibleCount, setVisibleCount] = useState(CHUNK);

  const [drawer, setDrawer] = useState({
    type: null,
    infrastructure: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchInfrastructures = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllInfrastructures();

      const list = extractArray(data).sort((a, b) => {
        const ordreA = Number(a.ordre_infrastructure ?? 999);
        const ordreB = Number(b.ordre_infrastructure ?? 999);

        if (ordreA !== ordreB) return ordreA - ordreB;

        return String(a.nom_infrastructure || "").localeCompare(
          String(b.nom_infrastructure || ""),
          "fr",
          { sensitivity: "base" }
        );
      });

      setInfrastructures(list);
    } catch (error) {
      console.error("Erreur lors de la récupération des infrastructures :", error);
      toast.error("Erreur lors de la récupération des infrastructures.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInfrastructures();
  }, [fetchInfrastructures]);

  const stats = useMemo(() => getStats(infrastructures), [infrastructures]);

  const filteredInfrastructures = useMemo(() => {
    const term = normalizeText(query.trim());

    return infrastructures.filter((infrastructure) => {
      const searchableText = [
        infrastructure.nom_infrastructure,
        infrastructure.adresse_infrastructure,
        infrastructure.description_infrastructure,
        infrastructure.gps_infrastructure,
      ]
        .filter(Boolean)
        .join(" ");

      const matchesSearch =
        !term || normalizeText(searchableText).includes(term);

      const hasGpsValue = Boolean(
        String(infrastructure.gps_infrastructure || "").trim()
      );

      const matchesGps =
        !gpsFilter ||
        (gpsFilter === "withGps" && hasGpsValue) ||
        (gpsFilter === "withoutGps" && !hasGpsValue);

      return matchesSearch && matchesGps;
    });
  }, [infrastructures, query, gpsFilter]);

  const visibleInfrastructures = useMemo(() => {
    return filteredInfrastructures.slice(0, visibleCount);
  }, [filteredInfrastructures, visibleCount]);

  const resetFilters = () => {
    setQuery("");
    setGpsFilter("");
    setVisibleCount(CHUNK);
  };

  const loadMore = () => {
    setVisibleCount((current) =>
      Math.min(current + CHUNK, filteredInfrastructures.length)
    );
  };

  const openCreateDrawer = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "create",
      infrastructure: null,
    });
  };

  const openEditDrawer = (infrastructure) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "edit",
      infrastructure,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      infrastructure: null,
    });
  };

  const handleCreateInfrastructure = async (payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await createInfrastructure(payload);

      toast.success("Infrastructure créée avec succès.");
      await fetchInfrastructures();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création de l'infrastructure :", error);
      toast.error("Erreur lors de la création de l'infrastructure.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInfrastructure = async (infrastructureId, payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await updateInfrastructure(infrastructureId, payload);

      toast.success("Infrastructure mise à jour avec succès.");
      await fetchInfrastructures();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'infrastructure :", error);
      toast.error("Erreur lors de la mise à jour de l'infrastructure.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInfrastructure = async (infrastructure) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    if (!infrastructure?.id) return;

    const confirmed = window.confirm(
      `Supprimer l'infrastructure "${infrastructure.nom_infrastructure}" ?`
    );

    if (!confirmed) return;

    try {
      await deleteInfrastructure(infrastructure.id);

      setInfrastructures((current) =>
        current.filter((item) => String(item.id) !== String(infrastructure.id))
      );

      toast.success("Infrastructure supprimée avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'infrastructure :", error);
      toast.error("Erreur lors de la suppression de l'infrastructure.");
    }
  };

  return {
    infrastructures,
    loading,
    saving,
    stats,
    query,
    gpsFilter,
    visibleInfrastructures,
    filteredInfrastructures,
    visibleCount,
    drawer,
    demoMode,

    setQuery,
    setGpsFilter,
    resetFilters,
    loadMore,
    refreshData: fetchInfrastructures,

    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateInfrastructure,
    handleUpdateInfrastructure,
    handleDeleteInfrastructure,
  };
}